/**
 * FieldRenderer — resolves inputType and renders the appropriate field component.
 *
 * Wraps in a responsive grid cell based on colSize.
 * Renders readOnly text span in view mode.
 *
 * @module components/form/fields/FieldRenderer
 * @see FMPV-MVWC-E003-S002 I-001
 */
import React from 'react';
import { Input } from '../../../../Input/Input';
import { Textarea } from '../../../../Textarea/Textarea';
import { Select } from '../../../../Select/Select';
import { LookupField } from '../../../../LookupField/LookupField';
import { interpolateTemplate } from '../../../../LookupField/utils';
import { MasterPatternViewCreateForm } from '../../MasterPatternViewCreateForm';
import type { MasterPatternViewEntity, MasterPatternViewCompany } from '../../../types/service.types';
import { FileUploader } from '../../../../FileUploader/FileUploader';
import type { UploadedFile } from '../../../../FileUploader/FileUploader.types';
import { Switch } from '../../../../Switch/Switch';
import { AlertTriangle } from 'lucide-react';
import { inferInputType, computeColSpanClass } from '../../../engine/fieldEngine';
import { isActionAllowed } from '../../../engine/permissionEngine';
import type { MasterPatternViewFieldDefinition, MasterPatternViewFileConfig } from '../../../types/definition.types';

type InitialFile = { id: string; url: string; name: string; size?: number; type?: string };
const EMPTY_FILES: InitialFile[] = [];

/**
 * Suffix for the shadow form field that mirrors a lookup's resolved label alongside its
 * `Id` — MasterPatternView.tsx's "Campos modificados" diff can only show a readable value
 * for a lookup selection made in the current session (rather than the raw guid) by reading
 * this, since a freshly-picked record isn't joined with display columns the way a persisted
 * row from the API is.
 */
export const LOOKUP_DISPLAY_FIELD_SUFFIX = '__display';

/** Same priority order as LookupField's own internal `getSelectedDisplay` (renderSelected is
 * not part of `MasterPatternViewLookupConfig`, so it's not in this priority list). */
function getLookupSelectedDisplay(
  cfg: { displayTemplate?: string; displayValue?: string; displayFields: string[] },
  record: Record<string, unknown>,
): string {
  if (cfg.displayTemplate) return interpolateTemplate(cfg.displayTemplate, record);
  if (cfg.displayValue) {
    const v = record[cfg.displayValue];
    return v !== undefined && v !== null ? String(v) : '';
  }
  return cfg.displayFields
    .map((f) => {
      const v = record[f];
      return v !== undefined && v !== null ? String(v) : '';
    })
    .join(' - ');
}

/**
 * Stable wrapper for FileUploader that avoids infinite re-render loops.
 * The initialFiles prop is memoized, and onFilesChange skips no-op updates.
 */
function FileField({ field, value, onChange, isDisabled, cfg }: {
  field: MasterPatternViewFieldDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
  isDisabled: boolean;
  cfg: MasterPatternViewFileConfig;
}) {
  const initialFiles = React.useMemo((): InitialFile[] => {
    if (Array.isArray(value) && value.length > 0) {
      return value as InitialFile[];
    }
    return EMPTY_FILES;
  }, [value]);

  const handleFilesChange = React.useCallback((files: UploadedFile[]) => {
    // Only propagate when files actually changed (has completed uploads or was cleared)
    const completed = files.filter(f => f.status === 'completed' || (f as { id?: string }).id);
    if (completed.length > 0 || (Array.isArray(value) && (value as unknown[]).length > 0 && files.length === 0)) {
      onChange(files);
    }
  }, [onChange, value]);

  // Use compact padding override when field has rowSpan (image beside short inputs)
  const compactClass = field.rowSpan ? '[&>div]:p-3 [&>div]:space-y-1' : undefined;

  return (
    <FileUploader
      data-testid={`field-input-${field.fieldName}`}
      uploadFunction={cfg.uploadFunction}
      accept={cfg.accept}
      maxSize={cfg.maxSize}
      multiple={cfg.multiple}
      variant={cfg.variant}
      autoUpload={cfg.autoUpload}
      initialFiles={initialFiles}
      onFilesChange={handleFilesChange}
      disabled={isDisabled}
      className={compactClass}
    />
  );
}

export interface FieldRendererProps {
  field: MasterPatternViewFieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** All current form values — passed to renderFormField for derived/computed fields */
  allValues?: Record<string, unknown>;
  /** Current form mode — passed as context to renderFormField */
  formMode?: 'create' | 'edit' | 'view';
  /** Multi-field setter forwarded to lookupConfig.onFormSelect for cascade updates */
  setFormValue?: (field: string, value: unknown) => void;
  /** This form's own company list — forwarded to a same-repo lookup's inline "create new" (`cfg.create`) so the child master's company selector starts seeded instead of always defaulting to Global. */
  companies?: MasterPatternViewCompany[];
  /** This form's own currently active company (undefined/'global' when in global context) — same forwarding as `companies`. */
  activeCompanyId?: string | null;
}

export function FieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled = false,
  readOnly = false,
  allValues = {},
  formMode,
  setFormValue,
  companies,
  activeCompanyId,
}: FieldRendererProps) {
  const inputType = field.inputType ?? inferInputType(field);
  const colSpanClass = computeColSpanClass(field);
  const rowSpanClass = field.rowSpan && field.rowSpan > 1 ? `row-span-${field.rowSpan}` : '';
  const strValue = value !== null && value !== undefined ? String(value) : '';

  // Memoize select options to avoid passing a new array reference on every render,
  // which would defeat Select's internal stability guarantees.
  const selectOptions = React.useMemo(
    () => (field.options ?? []).map((o) => ({ value: o.value, label: o.label })),
    [field.options]
  );

  const label = (
    <label className="block text-sm font-bold text-content-primary dark:text-dark-content-primary mb-2 leading-5" data-testid={`field-label-${field.fieldName}`}>
      {field.label}
      {field.isRequired && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  const errorEl = error ? (
    <p className="flex items-center gap-[3px] text-xs text-[#dc2626] mt-1" data-testid={`field-error-${field.fieldName}`}>
      <AlertTriangle size={11} className="flex-shrink-0" />
      {error}
    </p>
  ) : null;

  // In readOnly mode fields are rendered as disabled controls (identical layout to edit mode)
  const isDisabled = disabled || readOnly;

  // renderFormField escape hatch — consumer controls the entire input element
  if (field.renderFormField) {
    const resolvedMode = formMode ?? (readOnly ? 'view' : 'edit');
    return (
      <div data-testid={`field-wrapper-${field.fieldName}`} className={`${colSpanClass} ${rowSpanClass}`.trim()}>
        {label}
        {field.renderFormField(value, onChange, allValues, { readOnly, mode: resolvedMode })}
        {errorEl}
      </div>
    );
  }

  let inputEl: React.ReactNode;

  switch (inputType) {
    case 'boolean': {
      // Single toggle pill below the shared bold label (like every other field), instead of the
      // old two-button Sí/No segmented control. If field has custom `options`, show the current
      // state's label next to the switch instead of a second button.
      const hasCustomLabels = field.options && field.options.length >= 2;
      const trueLabel = hasCustomLabels ? field.options![0].label : null;
      const falseLabel = hasCustomLabels ? field.options![1].label : null;
      inputEl = (
        <div className="flex items-center gap-2 h-10" data-testid={`field-input-${field.fieldName}`}>
          <Switch
            checked={value === true}
            onCheckedChange={(checked) => !isDisabled && onChange(checked)}
            disabled={isDisabled}
            data-testid={`field-bool-switch-${field.fieldName}`}
          />
          {hasCustomLabels && (
            <span className="text-sm text-content-primary dark:text-dark-content-primary">
              {value === true ? trueLabel : falseLabel}
            </span>
          )}
        </div>
      );
      break;
    }

    case 'select':
      inputEl = (
        <Select
          data-testid={`field-input-${field.fieldName}`}
          options={selectOptions}
          value={value as string | number}
          onChange={onChange as (v: string | number) => void}
          disabled={isDisabled}
          selectSize="sm"
        />
      );
      break;

    case 'textarea':
      inputEl = (
        <Textarea
          data-testid={`field-input-${field.fieldName}`}
          value={strValue}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          disabled={isDisabled}
          fullWidth
        />
      );
      break;

    case 'date':
      inputEl = (
        <input
          type="date"
          data-testid={`field-input-${field.fieldName}`}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          disabled={isDisabled}
          className="w-full border border-border-primary dark:border-dark-border-primary rounded px-2 py-1 text-sm disabled:bg-background-secondary dark:disabled:bg-dark-bg-secondary disabled:text-content-tertiary dark:disabled:text-dark-content-tertiary disabled:cursor-not-allowed"
        />
      );
      break;

    case 'number':
    case 'decimal':
      inputEl = (
        <Input
          type="number"
          data-testid={`field-input-${field.fieldName}`}
          value={strValue}
          step={inputType === 'decimal' ? 'any' : '1'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(inputType === 'decimal' ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
          disabled={isDisabled}
        />
      );
      break;

    case 'lookup': {
      const cfg = field.lookupConfig;
      if (!cfg) {
        inputEl = (
          <Input
            data-testid={`field-input-${field.fieldName}`}
            value="[lookupConfig required]"
            disabled
          />
        );
      } else {
        inputEl = (
          <LookupField
            // Hides LookupField's own internal empty label — the label above (rendered by
            // this form) already shows the field name, and the empty one still takes up
            // ~20px (display:block by design), pushing the trigger down and misaligning it
            // against sibling fields in the same row.
            className="lookup-field--label-hidden"
            data-testid={`field-input-${field.fieldName}`}
            entity={cfg.entity}
            fetcher={cfg.fetcher}
            displayFields={cfg.displayFields}
            bindFields={cfg.bindFields}
            displayTemplate={cfg.displayTemplate}
            displayValue={cfg.displayValue}
            filters={cfg.getFilters ? cfg.getFilters(allValues) : cfg.filters}
            minChars={cfg.minChars}
            value={strValue || null}
            onChange={(rec, prevRec) => {
              onChange(rec?.Id ?? null);
              setFormValue?.(
                `${field.fieldName}${LOOKUP_DISPLAY_FIELD_SUFFIX}`,
                rec ? getLookupSelectedDisplay(cfg, rec) : null,
              );
              if (cfg.onFormSelect && setFormValue) {
                cfg.onFormSelect(rec ?? null, setFormValue, {
                  previousRecord: prevRec ?? null,
                  revert: () => {
                    onChange(prevRec?.Id ?? null);
                    setFormValue(
                      `${field.fieldName}${LOOKUP_DISPLAY_FIELD_SUFFIX}`,
                      prevRec ? getLookupSelectedDisplay(cfg, prevRec) : null,
                    );
                  },
                });
              }
            }}
            readOnly={readOnly}
            disabled={disabled}
            // Do NOT pass required — the label above already renders the asterisk.
            // Passing required here causes LookupField to render a second asterisk.
            createVariant={cfg.create?.variant}
            // No `.definition.name` fallback here on purpose: `useDefinition` must never
            // be called outside of MasterPatternViewCreateForm's own render (see its doc
            // comment) — calling it here, still inside the outer field's render, would
            // reintroduce the exact self-referencing-lookup recursion this design avoids.
            // Omit `title`/`entityName` and LookupField falls back to a generic label.
            createTitle={cfg.create?.title ?? cfg.create?.entityName}
            createButtonLabel={cfg.create?.buttonLabel}
            // Default width is always 80% of the viewport for modal creates — a fixed,
            // consistent footprint across every master. Height is NOT fixed the same way:
            // it's a CAP (80vh) that the popup only grows into if the content needs it —
            // CreatePopup treats `height` as max-height only, so a 3-field master shrinks
            // to fit while a many-tab master can use the full 80vh. Sidebar keeps its own
            // compact width default (edge-panel, not meant to span most of the screen).
            createWidth={cfg.create?.width ?? (cfg.create?.variant === 'sidebar' ? undefined : '80vw')}
            createHeight={cfg.create?.height ?? (cfg.create?.variant === 'sidebar' ? undefined : '80vh')}
            createIcon={cfg.create?.icon}
            // Gated by the CHILD entity's own `cfg.create.permissions` (e.g. Teléfono's
            // canCreate), never by this field's own master's permissions — see the doc
            // comment on `create.permissions`. Defaults to allowed when omitted, same as
            // every other permission check in the kit.
            showCreateButton={
              cfg.create?.showCreateButton === false
                ? false
                : isActionAllowed(cfg.create?.permissions, 'CREATE', 'UNIVERSAL')
            }
            // MasterPatternViewCreateForm renders its own complete header (title,
            // Cancelar/Guardar, "Creando" badge) — the popup's own title bar would just
            // duplicate it. The cross-repo `onCreateNew` escape hatch below usually owns
            // none of that itself, so it defaults to the native header (with createTitle)
            // instead — explicit `true`/`false` here rather than leaning on LookupField's
            // own default, since that default is about ITS assumption (a same-repo
            // MasterPatternViewCreateForm-shaped consumer), not this cross-repo one.
            // `create.showHeader` still lets either path override the default, e.g. for
            // an onCreateNew render function that DOES bring its own header.
            createShowHeader={
              cfg.create?.showHeader !== undefined
                ? cfg.create.showHeader
                : cfg.create?.onCreateNew
                  ? true
                  : false
            }
            onCreateNew={
              cfg.create?.onCreateNew
                // Cross-repo / fully custom path — same-repo useDefinition+service can't
                // reach a target master that lives in a different build, so this passes
                // straight through to LookupField's own generic onCreateNew contract.
                ? cfg.create.onCreateNew
                // Gate the local fallback on `useDefinition` specifically, NOT on `cfg.create`
                // being truthy — a consumer using the cross-repo `onCreateNew` escape hatch
                // never sets `useDefinition` (see its doc comment: "omit useDefinition/service
                // in that case"), so when that escape hatch resolves to `undefined` (e.g. a
                // host-side kill switch temporarily disabling nested creation), `cfg.create`
                // itself is still a truthy object — falling back to this same-repo form here
                // would try to mount MasterPatternViewCreateForm with `useDefinition`/`service`
                // both undefined, and — because the arrow function below is unconditionally
                // truthy — would keep showing LookupField's "+" button even though creation was
                // just explicitly turned off. Only ever fall back here when the consumer
                // genuinely opted into the same-repo path.
                : cfg.create?.useDefinition
                  ? ({ onCreated, onCancel }) => (
                      <MasterPatternViewCreateForm<MasterPatternViewEntity>
                        useDefinition={cfg.create!.useDefinition!}
                        service={cfg.create!.service!}
                        entityName={cfg.create!.entityName}
                        permissions={cfg.create!.permissions}
                        // Wear the same background/shadow/radius as the master's own
                        // ModalPanel/SidebarPanel, so the popup shows it exactly like the
                        // real create modal/sidebar instead of a foreign popup shell.
                        chrome={cfg.create!.variant ?? 'modal'}
                        // This IS the one call site that wants the child's company locked
                        // by default: the record being created here belongs to THIS
                        // field's parent record's company, not to whatever the child
                        // master's own create form would otherwise default to (its own
                        // companies[0], which reads as "Global" to the user). Only locks
                        // when the parent itself is actually scoped to a specific company —
                        // a parent still in Global context has nothing specific to lock to,
                        // so the child's selector stays open in that case.
                        companies={companies}
                        defaultCompany={companies?.find((c) => c.id === activeCompanyId)}
                        companyLocked={!!activeCompanyId}
                        onCancel={onCancel}
                        onCreated={(record) => {
                          // Bridge casing conventions: MasterPatternViewService records use
                          // camelCase `id` (MasterPatternViewEntity), but LookupField's own
                          // selection contract needs PascalCase `Id` (LookupRecord) to work —
                          // alias it on without touching any other field.
                          const asLookupRecord = {
                            ...(record as unknown as Record<string, unknown>),
                            Id: String((record as unknown as Record<string, unknown>).id),
                          };
                          // service.create()'s response is shaped by this master's own API,
                          // which can (and often does) differ from what THIS lookup's own
                          // search `fetcher` returns for the same entity — e.g. a reverse
                          // cascade `onFormSelect` reading `rec.CountryID`/`rec.StateID`
                          // (fields the search fetcher injects) would silently get
                          // `undefined` from the raw create response, so the dependent
                          // fields it's meant to fill never activate. Re-fetch by Id through
                          // the same fetcher used for search, so the just-created record
                          // reaches `onCreated`/`onFormSelect` in the exact same shape as one
                          // picked from the dropdown — falling back to the bridged create
                          // response only if that lookup fails (e.g. read-after-write lag).
                          cfg
                            .fetcher(cfg.entity, {
                              fields: [...new Set(['Id', ...cfg.displayFields, ...(cfg.bindFields ?? [])])],
                              filters: [[{ Id: asLookupRecord.Id as string }]],
                              page: 1,
                              pageSize: 1,
                            })
                            .then((response) => {
                              const fetched = response.data[0] as Record<string, unknown> | undefined;
                              onCreated(fetched ? { ...fetched, Id: asLookupRecord.Id } : asLookupRecord);
                            })
                            .catch(() => onCreated(asLookupRecord));
                        }}
                      />
                    )
                  : undefined
            }
          />
        );
      }
      break;
    }

    case 'email':
    case 'password':
      inputEl = (
        <Input
          data-testid={`field-input-${field.fieldName}`}
          type={inputType}
          value={strValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={disabled}
          required={field.isRequired}
          maxLength={field.maxLength}
        />
      );
      break;

    case 'file': {
      const cfg = field.fileConfig;
      if (!cfg) {
        inputEl = (
          <Input
            data-testid={`field-input-${field.fieldName}`}
            value="[fileConfig required]"
            disabled
          />
        );
      } else {
        inputEl = (
          <FileField
            field={field}
            value={value}
            onChange={onChange}
            isDisabled={isDisabled}
            cfg={cfg}
          />
        );
      }
      break;
    }

    default: // 'text'
      inputEl = (
        <Input
          data-testid={`field-input-${field.fieldName}`}
          value={strValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          disabled={isDisabled}
          maxLength={field.maxLength}
        />
      );
  }

  return (
    <div data-testid={`field-wrapper-${field.fieldName}`} className={`${colSpanClass} ${rowSpanClass}`.trim()}>
      {label}
      {inputEl}
      {errorEl}
    </div>
  );
}

